import { useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { BookOpenCheck, CheckCircle2, ShieldCheck, Sparkles, XCircle } from 'lucide-react'
import { AppChrome } from '../components/AppChrome'
import {
  EmptyState,
  ErrorState,
  LoadingState,
  StatTile,
} from '../components/DataViz'
import {
  fetchSkillEvolutions,
  fetchSkillValidation,
  sendJson,
  type SkillValidationRow,
} from '../lib/api'
import { useAuth } from '../lib/auth'
import { useAsync } from '../lib/useAsync'

/**
 * Learning (AFOS-41): how skills improve, and who signs off.
 * - Platform phase skills: validation evidence (static floor + scenarios) and the
 *   named-human review of each skill's exact content digest (platform super admin).
 * - Skill evolutions proposed from team learnings: quarantined until a person with
 *   learning.promote reviews the body digest and promotes it (in Teams).
 * Bodies are untrusted text and are rendered as plain text, never as HTML.
 */
export function LearningPage() {
  const { user, loading } = useAuth()
  const skills = useAsync(fetchSkillValidation, !!user)
  const evolutions = useAsync(fetchSkillEvolutions, !!user)
  const [message, setMessage] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [open, setOpen] = useState<string | null>(null)

  if (!loading && !user) return <Navigate to="/signin" replace />

  const perms = new Set(user?.permissions ?? [])
  const superAdmin = !!user?.is_super_admin
  const canHarvest = superAdmin || perms.has('learning.promote')

  async function review(row: SkillValidationRow) {
    if (!row.content_digest) return
    const ok = window.confirm(
      `Approve the current content of "${row.skill_id}"?\n\nDigest ${row.content_digest.slice(0, 16)}…\nThis records your name against this exact content.`,
    )
    if (!ok) return
    setBusy(row.skill_id)
    setMessage(null)
    try {
      await sendJson(
        `/api/afos360/v1/skill-validation/${encodeURIComponent(row.skill_id)}/review`,
        'POST',
        { confirm: 'APPROVE_SKILL', content_digest: row.content_digest },
        'Review failed',
      )
      setMessage(`Recorded your review of ${row.skill_id}.`)
      await skills.reload()
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Review failed')
    } finally {
      setBusy(null)
    }
  }

  async function harvest() {
    setBusy('harvest')
    setMessage(null)
    try {
      const out = await sendJson<{ harvested?: number }>(
        '/api/afos360/v1/skill-evolutions/harvest',
        'POST',
        {},
        'Harvest failed',
      )
      setMessage(`Harvested ${out.harvested ?? 0} proposal(s) into quarantine.`)
      await evolutions.reload()
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Harvest failed')
    } finally {
      setBusy(null)
    }
  }

  const rows = skills.data?.skills ?? []
  const passed = rows.filter((r) => r.last_passed && !r.stale).length
  const reviewed = rows.filter((r) => r.reviewed).length
  const awaiting = rows.filter((r) => r.last_passed && !r.stale && !r.reviewed)

  return (
    <AppChrome>
      <main className="afos-page space-y-8">
        <header>
          <h1 className="afos-page-title">Learning</h1>
          <p className="afos-page-lead">
            Skills improve from what squads learn, but nothing changes agent behaviour until a named
            person approves the exact content. Static checks are a floor; the human review is the
            gate.
          </p>
        </header>

        {message && (
          <div role="status" className="rounded-2xl border border-[var(--color-line)] bg-black/25 px-4 py-3 text-sm">
            {message}
          </div>
        )}

        <section aria-labelledby="platform-skills" className="space-y-4">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <h2 id="platform-skills" className="font-[family-name:var(--font-display)] text-2xl font-extrabold">
              Platform skills
            </h2>
            <Link to="/evaluations" className="text-sm text-[var(--color-cyan)] hover:underline">
              Validation results →
            </Link>
          </div>
          {skills.error && !skills.data && <ErrorState message={skills.error} onRetry={() => void skills.reload()} />}
          {!skills.data && !skills.error && <LoadingState label="Loading platform skills" />}
          {skills.data && (
            <>
              <div className="grid gap-4 sm:grid-cols-3">
                <StatTile
                  label="Passing, current"
                  value={`${passed}/${rows.length}`}
                  icon={<CheckCircle2 aria-hidden="true" className="h-5 w-5 text-[var(--color-cyan)]" />}
                />
                <StatTile
                  label="Approved by a person"
                  value={reviewed}
                  icon={<ShieldCheck aria-hidden="true" className="h-5 w-5 text-[var(--color-cyan)]" />}
                />
                <StatTile
                  emphasis={awaiting.length > 0}
                  label="Awaiting review"
                  value={awaiting.length}
                  hint={superAdmin ? 'You can approve these below.' : 'Platform super admins approve these.'}
                  icon={<BookOpenCheck aria-hidden="true" className="h-5 w-5 text-[var(--color-amber)]" />}
                />
              </div>
              {rows.length === 0 ? (
                <EmptyState>No phase skills found.</EmptyState>
              ) : (
                <div className="glass afos-scroll-x rounded-3xl">
                  <table className="w-full min-w-[44rem] text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wider text-[var(--color-mist)]">
                        <th className="px-5 py-3 font-semibold">Skill</th>
                        <th className="px-3 py-3 font-semibold">Validation</th>
                        <th className="px-3 py-3 font-semibold">Review</th>
                        <th className="px-3 py-3 font-semibold">Publishable</th>
                        <th className="px-5 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.skill_id} id={`skill-${r.skill_id}`} className="border-t border-[var(--color-line)]">
                          <td className="px-5 py-3">
                            <div className="font-semibold text-[var(--color-foam)]">{r.skill_id}</div>
                            <div className="font-mono text-xs text-[var(--color-mist)]">
                              {r.content_digest ? `${r.content_digest.slice(0, 12)}…` : 'digest unavailable'}
                            </div>
                          </td>
                          <td className="px-3 py-3">
                            <Status
                              ok={!!r.last_passed && !r.stale}
                              label={
                                r.stale
                                  ? 'Changed since last run'
                                  : r.last_passed
                                    ? 'Passed'
                                    : r.last_passed === false
                                      ? 'Failed'
                                      : 'Not run'
                              }
                            />
                          </td>
                          <td className="px-3 py-3 text-[var(--color-mist)]">
                            {r.reviewed ? `Approved by ${r.reviewed_by ?? 'a reviewer'}` : 'Not reviewed'}
                          </td>
                          <td className="px-3 py-3">
                            <Status ok={r.publishable} label={r.publishable ? 'Yes' : 'No'} />
                          </td>
                          <td className="px-5 py-3 text-right">
                            {superAdmin && r.last_passed && !r.stale && !r.reviewed && (
                              <button
                                type="button"
                                disabled={busy === r.skill_id}
                                onClick={() => void review(r)}
                                className="rounded-full bg-[var(--color-amber)] px-3 py-1.5 text-xs font-semibold text-[var(--color-void)] disabled:opacity-50"
                              >
                                {busy === r.skill_id ? 'Recording…' : 'Approve content'}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </section>

        <section aria-labelledby="evolutions" className="space-y-4">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 id="evolutions" className="font-[family-name:var(--font-display)] text-2xl font-extrabold">
                Skill evolutions
              </h2>
              <p className="text-sm text-[var(--color-mist)]">
                Proposed from what teams learn. Quarantined until promoted by a named person in{' '}
                <Link to="/teams" className="text-[var(--color-cyan)] hover:underline">
                  Teams
                </Link>
                .
              </p>
            </div>
            {canHarvest && (
              <button
                type="button"
                disabled={busy === 'harvest'}
                onClick={() => void harvest()}
                className="inline-flex items-center gap-2 rounded-full border border-[var(--color-line)] px-4 py-2 text-sm text-[var(--color-foam)] hover:bg-white/5 disabled:opacity-50"
              >
                <Sparkles aria-hidden="true" className="h-4 w-4 text-[var(--color-cyan)]" />
                {busy === 'harvest' ? 'Harvesting…' : 'Harvest new proposals'}
              </button>
            )}
          </div>
          {evolutions.error && !evolutions.data && (
            <ErrorState message={evolutions.error} onRetry={() => void evolutions.reload()} />
          )}
          {!evolutions.data && !evolutions.error && <LoadingState label="Loading skill evolutions" />}
          {evolutions.data && (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                <StatTile label="Quarantined" value={evolutions.data.quarantined} hint="Awaiting a named person" />
                <StatTile label="Promoted" value={evolutions.data.promoted} hint="Live in the skill library" />
              </div>
              {evolutions.data.evolutions.length === 0 ? (
                <EmptyState>
                  No evolutions yet. They appear after teams share learnings and a harvest runs.
                </EmptyState>
              ) : (
                <ul className="space-y-3">
                  {evolutions.data.evolutions.map((e) => (
                    <li key={e.evolution_id} id={`evolution-${e.evolution_id}`} className="glass rounded-3xl p-5">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--color-mist)]">
                            {e.status.replace(/_/g, ' ')} · gen {e.generation ?? '—'}
                            {e.confidence != null ? ` · confidence ${Math.round(Number(e.confidence) * 100)}%` : ''}
                          </div>
                          <div className="mt-1 text-base font-semibold text-[var(--color-foam)]">
                            {e.title || e.evolution_id}
                          </div>
                          <div className="font-mono text-xs text-[var(--color-mist)]">
                            body {e.body_sha256 ? `${e.body_sha256.slice(0, 12)}…` : '—'}
                          </div>
                        </div>
                        <button
                          type="button"
                          aria-expanded={open === e.evolution_id}
                          onClick={() => setOpen(open === e.evolution_id ? null : e.evolution_id)}
                          className="rounded-full border border-[var(--color-line)] px-3 py-1.5 text-xs text-[var(--color-mist)] hover:text-[var(--color-foam)]"
                        >
                          {open === e.evolution_id ? 'Hide body' : 'Show body'}
                        </button>
                      </div>
                      {open === e.evolution_id && (
                        <pre className="mt-4 max-h-80 overflow-auto whitespace-pre-wrap rounded-2xl bg-black/30 p-4 text-xs text-[var(--color-foam)]">
                          {e.body_md || '(empty)'}
                        </pre>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </section>
      </main>
    </AppChrome>
  )
}

function Status({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[var(--color-foam)]">
      {ok ? (
        <CheckCircle2 aria-hidden="true" className="h-4 w-4 text-[var(--color-cyan)]" />
      ) : (
        <XCircle aria-hidden="true" className="h-4 w-4 text-[var(--color-mist)]" />
      )}
      {label}
    </span>
  )
}
